import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getAttribution } from "@/lib/tracking";
import { loadFacebookPixel, trackFacebookLead } from "@/lib/fbPixel";
import { QuizProgress } from "@/components/quiz/QuizProgress";
import { QuizOptions } from "@/components/quiz/QuizOptions";
import { ContactForm } from "@/components/quiz/ContactForm";
import { QuizNav } from "@/components/quiz/QuizNav";
import { ResultScreen } from "@/components/quiz/ResultScreen";

export default function Quiz() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    first_name: "", last_name: "", email: "", phone: "", postal_code: "", member_status: "", recontact_ok: false,
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [partialId, setPartialId] = useState(null);

  useEffect(() => {
    api.get(`/questionnaire?lang=${lang}`)
      .then((res) => setQuestions(res.data.questions))
      .catch(() => toast.error(t("err_generic")));
  }, [lang, t]);

  // Facebook Pixel — only on /test, only after cookie consent (GDPR-aligned).
  useEffect(() => {
    if (localStorage.getItem("bh_consent") === "granted") loadFacebookPixel();
    const onGrant = () => loadFacebookPixel();
    window.addEventListener("bh-consent-granted", onGrant);
    return () => window.removeEventListener("bh-consent-granted", onGrant);
  }, []);

  // Persist partial progress so abandoned tests are still captured as leads.
  useEffect(() => {
    if (!partialId || result) return;
    const handle = setTimeout(() => {
      api.patch(`/leads/${partialId}/partial`, { answers }).catch(() => {});
    }, 800);
    return () => clearTimeout(handle);
  }, [answers, partialId, result]);

  if (!questions.length) {
    return <div className="min-h-screen flex items-center justify-center text-[#4b5b7c]">{t("loading")}</div>;
  }

  const total = questions.length;
  const current = questions[step];
  const progress = Math.round(((step + 1) / total) * 100);
  const isContact = current.type === "contact";
  const isLast = step === total - 1;
  const hasAnswer = answers[current.id] !== undefined;

  const choose = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => setStep((s) => (s < total - 1 ? s + 1 : s)), 220);
  };

  const startQuestionnaire = async () => {
    if (!contact.first_name || !contact.email || !contact.phone) {
      toast.error(t("err_required"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
      toast.error(t("err_email_invalid"));
      return;
    }
    if (!consent) {
      toast.error(t("err_consent"));
      return;
    }
    // Capture the lead now (with consent) so we keep it even if the test is abandoned.
    // Skip if a partial lead already exists (e.g. user went back) to avoid duplicates.
    if (partialId) {
      setStep((s) => s + 1);
      return;
    }
    try {
      const attribution = getAttribution();
      const params = new URLSearchParams(window.location.search);
      const source = attribution.source || params.get("source") || "";
      const { data } = await api.post("/leads/partial", {
        answers, contact, consent, source, lang,
        utm_source: attribution.utm_source || "",
        utm_medium: attribution.utm_medium || "",
        utm_campaign: attribution.utm_campaign || "",
        referrer: attribution.referrer || "",
      });
      setPartialId(data.lead_id);
      fireLeadConversion();
      trackFacebookLead();
    } catch (e) {
      /* non-blocking: never prevent the user from taking the test */
    }
    setStep((s) => s + 1);
  };

  const selectedOpt = current?.options?.find((o) => o.value === answers[current.id]);
  const showSafety = !!selectedOpt?.safety;

  // Google Ads — "Envoi de formulaire de lead" conversion (fires on real submission only).
  const fireLeadConversion = () => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-992700059/tSGXCLaJwc8cEJvNrdkD",
        value: 1.0,
        currency: "EUR",
      });
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const attribution = getAttribution();
      const params = new URLSearchParams(window.location.search);
      const source = attribution.source || params.get("source") || "";
      const { data } = await api.post("/leads", {
        answers, contact, consent, source, lang,
        utm_source: attribution.utm_source || "",
        utm_medium: attribution.utm_medium || "",
        utm_campaign: attribution.utm_campaign || "",
        referrer: attribution.referrer || "",
        lead_id: partialId || "",
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ResultScreen result={result} />;

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col">
      <QuizProgress step={step} total={total} progress={progress} />

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.3 }}
          >
            <span className="text-sm font-bold uppercase tracking-wider text-[#9aa6c0]">{current.section}</span>
            <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#1a2d56] mt-3 leading-snug">
              {current.question}
            </h1>

            {isContact && <p className="mt-3 text-[#4b5b7c]">{t("contact_subtitle")}</p>}

            {!isContact && (
              <QuizOptions current={current} answers={answers} onChoose={choose} showSafety={showSafety} />
            )}

            {isContact && (
              <ContactForm
                contact={contact}
                setContact={setContact}
                consent={consent}
                setConsent={setConsent}
                onStart={startQuestionnaire}
              />
            )}

            {!isContact && isLast && hasAnswer && (
              <button
                data-testid="submit-quiz-button"
                onClick={submit}
                disabled={submitting}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-head font-bold rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-60"
              >
                {submitting ? <RefreshCw size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {submitting ? t("generating") : t("submit_btn")}
              </button>
            )}
          </motion.div>
        </AnimatePresence>

        <QuizNav
          isContact={isContact}
          isLast={isLast}
          hasAnswer={hasAnswer}
          onBack={() => (step === 0 ? navigate("/") : setStep((s) => s - 1))}
          onNext={() => hasAnswer && setStep((s) => Math.min(s + 1, total - 1))}
        />
      </main>
    </div>
  );
}
